import { useLayoutEffect, useMemo, useRef } from 'react'

type Handler = (...args: never[]) => unknown

/**
 * A set of event handlers whose identities never change, each calling the latest version passed
 * in. The inventory list hands one of these to every (memoised) row: handlers rebuilt on each page
 * render — the page re-renders per keystroke, per selection toggle — used to give every visible
 * row new props and re-render them all.
 *
 * Only for handlers called from events, after commit (the usual "latest ref" caveat). The set of
 * keys must stay the same for the component's life.
 */
export function useStableHandlers<T extends Record<string, Handler>>(handlers: T): T {
  const latest = useRef(handlers)
  useLayoutEffect(() => {
    latest.current = handlers
  })
  return useMemo(() => {
    const stable: Partial<Record<keyof T, Handler>> = {}
    for (const key of Object.keys(handlers) as Array<keyof T>) {
      stable[key] = (...args: never[]) => latest.current[key](...args)
    }
    return stable as T
    // The keys are fixed; the bodies are read through `latest` at call time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}
